import {
  orchestrator,
  useAuth,
  type CallTypeRecord,
  type InstructionRecord,
} from "@savvy/runtime";
import {
  Badge,
  Button,
  FormBuilder,
  Input,
  validateFormFields,
  type FormField,
} from "@savvy/ui";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Call types for one client, and the ordered instructions behind each
 * (BACKLOG §12 slice 2). The agent sees these as radio options after
 * answering; picking one shows its steps.
 *
 * Order is edited with up/down buttons rather than drag: the reorder
 * endpoints take the WHOLE list, which makes a swap trivial and atomic, and
 * there is no drag library in this app to reach for.
 */

const errText = "rounded-md bg-danger-bg px-2.5 py-1.5 text-sm text-danger";

/** Swap idx with its neighbour and return the full new order, or null at an end. */
function movedOrder(list: { id: string }[], idx: number, delta: number): string[] | null {
  const to = idx + delta;
  if (to < 0 || to >= list.length) return null;
  const next = [...list];
  const a = next[idx]!;
  const b = next[to]!;
  next[idx] = b;
  next[to] = a;
  return next.map((x) => x.id);
}

export function CallTypesEditor({ clientId }: { clientId: string }) {
  const api = useAuth((s) => s.api);
  const [types, setTypes] = useState<CallTypeRecord[]>([]);
  // Which call type has a panel open, and which panel. A call type owns two
  // editable things now — the steps the agent works through and the form they
  // fill — and only one is open at a time.
  const [open, setOpen] = useState<{ id: string; panel: "steps" | "form" } | null>(null);
  const [steps, setSteps] = useState<Record<string, InstructionRecord[]>>({});
  const [newType, setNewType] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTypes(await orchestrator(api).listCallTypes(clientId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [api, clientId]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, [load]);

  const loadSteps = useCallback(
    async (ctId: string) => {
      try {
        const list = await orchestrator(api).listInstructions(ctId);
        setSteps((m) => ({ ...m, [ctId]: list }));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [api],
  );

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const reorderTypes = (idx: number, delta: number) => {
    const ids = movedOrder(types, idx, delta);
    if (!ids) return;
    void run(async () => {
      await orchestrator(api).reorderCallTypes(clientId, ids);
      await load();
    });
  };

  return (
    <div className="py-1">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Input
          className="w-56"
          placeholder="New call type (e.g. New client)"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={busy || newType.trim() === ""}
          onClick={() =>
            void run(async () => {
              await orchestrator(api).createCallType(clientId, newType.trim());
              setNewType("");
              await load();
            })
          }
        >
          Add call type
        </Button>
        <span className="text-xs text-muted-foreground">
          The agent picks one of these after answering; its instructions appear underneath.
        </span>
      </div>

      {types.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No call types — the agent sees the greeting only, exactly as before.
        </p>
      )}

      {types.map((t, i) => (
        <div key={t.id} className="mb-1.5 rounded-md border">
          <div className="flex flex-wrap items-center gap-1.5 p-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy || i === 0}
              title="Move up"
              onClick={() => reorderTypes(i, -1)}
            >
              ↑
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || i === types.length - 1}
              title="Move down"
              onClick={() => reorderTypes(i, 1)}
            >
              ↓
            </Button>
            <Input
              className="w-52"
              defaultValue={t.name}
              onBlur={(e) => {
                const name = e.target.value.trim();
                if (name && name !== t.name) {
                  void run(async () => {
                    await orchestrator(api).updateCallType(t.id, { name });
                    await load();
                  });
                }
              }}
            />
            {!t.enabled && <Badge variant="warning">disabled</Badge>}
            <Button
              variant={open?.id === t.id && open.panel === "steps" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                const on = open?.id === t.id && open.panel === "steps";
                setOpen(on ? null : { id: t.id, panel: "steps" });
                if (!on) void loadSteps(t.id);
              }}
            >
              Instructions{steps[t.id] ? ` (${steps[t.id]!.length})` : ""}
            </Button>
            <Button
              variant={open?.id === t.id && open.panel === "form" ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setOpen(
                  open?.id === t.id && open.panel === "form"
                    ? null
                    : { id: t.id, panel: "form" },
                )
              }
            >
              Form{t.fields.length ? ` (${t.fields.length})` : ""}
            </Button>
            {/* Disable rather than delete keeps history readable: past calls
                still name a type the agent can no longer pick. */}
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await orchestrator(api).updateCallType(t.id, { enabled: !t.enabled });
                  await load();
                })
              }
            >
              {t.enabled ? "Disable" : "Enable"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await orchestrator(api).deleteCallType(t.id);
                  await load();
                })
              }
            >
              Delete
            </Button>
          </div>

          {open?.id === t.id && open.panel === "form" && (
            <FormPanel
              callType={t}
              busy={busy}
              onSave={(fields) =>
                run(async () => {
                  await orchestrator(api).updateCallType(t.id, { fields });
                  await load();
                })
              }
            />
          )}

          {open?.id === t.id && open.panel === "steps" && (
            <InstructionsEditor
              steps={steps[t.id] ?? []}
              busy={busy}
              onAdd={(body) =>
                void run(async () => {
                  await orchestrator(api).addInstruction(t.id, body);
                  await loadSteps(t.id);
                })
              }
              onEdit={(id, body) =>
                void run(async () => {
                  await orchestrator(api).updateInstruction(id, body);
                  await loadSteps(t.id);
                })
              }
              onDelete={(id) =>
                void run(async () => {
                  await orchestrator(api).deleteInstruction(id);
                  await loadSteps(t.id);
                })
              }
              onMove={(idx, delta) => {
                const ids = movedOrder(steps[t.id] ?? [], idx, delta);
                if (!ids) return;
                void run(async () => {
                  await orchestrator(api).reorderInstructions(t.id, ids);
                  await loadSteps(t.id);
                });
              }}
            />
          )}
        </div>
      ))}
      {err && <p className={errText}>{err}</p>}
    </div>
  );
}

function InstructionsEditor({
  steps,
  busy,
  onAdd,
  onEdit,
  onDelete,
  onMove,
}: {
  steps: InstructionRecord[];
  busy: boolean;
  onAdd: (body: string) => void;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  onMove: (idx: number, delta: number) => void;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    if (draft.trim() === "") return;
    onAdd(draft.trim());
    setDraft("");
  };

  return (
    <div className="border-t bg-muted/30 p-2">
      <ol className="mb-2 list-decimal space-y-1 pl-6">
        {steps.map((s, i) => (
          <li key={s.id}>
            <div className="flex flex-wrap items-center gap-1.5">
              <Input
                className="min-w-72 flex-1"
                defaultValue={s.body}
                onBlur={(e) => {
                  const body = e.target.value.trim();
                  if (body && body !== s.body) onEdit(s.id, body);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={busy || i === 0}
                title="Move up"
                onClick={() => onMove(i, -1)}
              >
                ↑
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy || i === steps.length - 1}
                title="Move down"
                onClick={() => onMove(i, 1)}
              >
                ↓
              </Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => onDelete(s.id)}>
                ×
              </Button>
            </div>
          </li>
        ))}
      </ol>
      {steps.length === 0 && (
        <p className="mb-2 pl-1 text-sm text-muted-foreground">
          No instructions yet — the agent sees this call type with nothing under it.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <Input
          className="min-w-72 flex-1"
          placeholder="Next step for the agent…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
        />
        <Button variant="outline" size="sm" disabled={busy || draft.trim() === ""} onClick={add}>
          Add step
        </Button>
      </div>
    </div>
  );
}

/**
 * The form for one call type. The builder is controlled and holds no server
 * state, so this panel owns the draft and the save — the builder never learns
 * that an API exists.
 *
 * Saving is explicit rather than per-keystroke: a form is read by an agent
 * mid-call, and a half-edited document reaching them is worse than a save
 * button.
 */
function FormPanel({
  callType,
  busy,
  onSave,
}: {
  callType: CallTypeRecord;
  busy: boolean;
  onSave: (fields: FormField[]) => Promise<void>;
}) {
  const [draft, setDraft] = useState<FormField[]>(callType.fields as FormField[]);
  const [notice, setNotice] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(callType.fields);

  return (
    <div className="mt-3 flex flex-col gap-3 border-t pt-3">
      <FormBuilder
        fields={draft}
        onChange={setDraft}
        formTitle={callType.name}
        notice={notice}
        disabled={busy}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={busy || !dirty}
          onClick={() => {
            const problem = validateFormFields(draft);
            if (problem) {
              setNotice(problem);
              return;
            }
            setNotice(null);
            void onSave(draft);
          }}
        >
          Save form
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || !dirty}
          onClick={() => {
            setDraft(callType.fields as FormField[]);
            setNotice(null);
          }}
        >
          Discard changes
        </Button>
        {dirty && <span className="text-sm text-muted-foreground">Unsaved changes</span>}
      </div>
    </div>
  );
}
